import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { ASSISTANT_GROUP_TAG } from '@crowdex/agent-contracts/api';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import { useCallback, useMemo } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { hasPendingToolApproval } from '@/lib/conversations/message-part/tool-approval';
import { isCloudFolder } from '@/lib/desktop-paths';
import { PREVIEW_THREADS } from '@/lib/desktop-preview-data';
import { workspaceName } from '@/lib/desktop-shell/conversation-worktree';
import { runtimeMode } from '@/lib/desktop-shell/execution-mode-controls';
import { PERSONAL_WORKSPACE_NAME } from '@/lib/desktop-shell/personal-workspace';
import { isProjectRemoved } from '@/lib/desktop-shell/project-order';
import {
  buildRepositoryOptions,
  type RepositoryOrigin,
} from '@/lib/desktop-shell/repository-options';
import type { DesktopShellStore, SyncedWorkspace } from '@/lib/desktop-shell/store';
import { isLocalMachine } from '@/lib/local-runtime';
import type { NativeGitStatus } from '@/lib/native-desktop';
import { buildProjectDirectory, type SidebarProjectDirectory } from '@/lib/sidebar-projects';
import {
  buildProjectGroups,
  buildProjectPaths,
  buildSidebarThreads,
  logicalWorkspaceRepositoryFullName,
  logicalWorkspaceRepositoryRemote,
  type SidebarThread,
  sourceControlRepositoryFullName,
} from '@/lib/sidebar-threads';

type SidebarPreferences = {
  /** Repositories the user removed from the sidebar (kept by the sync bootstrap). */
  removedProjects: ReadonlySet<string>;
};

const NO_PINNED_PROJECTS: ReadonlySet<string> = new Set();

/**
 * Where a repository of the picker is: its folder on this computer or only the cloud's copy (the
 * cloud's folders are under `/workspace`), and the forge repository its project clones, by the
 * cloud's folder or the project's sync key.
 */
function repositoryOrigin(
  path: string,
  projects: SidebarProjectDirectory,
  workspaces: readonly SyncedWorkspace[],
): RepositoryOrigin {
  const project = projects.ofRoot(path);
  const logicalWorkspaceId =
    project.key === 'none' || project.key.startsWith('path:') ? null : project.key;
  const syncKey =
    logicalWorkspaceId === null
      ? undefined
      : workspaces.find((entry) => entry.logicalWorkspaceId === logicalWorkspaceId && entry.syncKey)
          ?.syncKey;
  const fullName =
    project.roots.map(sourceControlRepositoryFullName).find((name) => name !== null) ??
    logicalWorkspaceRepositoryFullName(syncKey);
  return {
    onThisComputer: !isCloudFolder(path),
    forge:
      fullName === null || fullName === undefined
        ? null
        : { fullName, remote: logicalWorkspaceRepositoryRemote(syncKey) },
    logicalWorkspaceId,
  };
}

/**
 * Derives all project/thread collections consumed by the desktop sidebar. Projects come from the
 * server's workspace records (`sidebar-projects`): a worktree is filed under its repository on
 * every device, and two repositories of one name stay two projects.
 */
/** A group call's conversation (assistants together), which Live shows. */
function isAssistantGroup(thread: SidebarThread): boolean {
  return thread.tags?.includes(ASSISTANT_GROUP_TAG) === true;
}

export function useSidebarModel(options: {
  store: DesktopShellStore;
  preview: boolean;
  worktreeSourceRoot: string | null;
  workspace: string | null;
  messages: readonly CrowdexUIMessage[];
  conversationTitle: string;
  cloudAgentSelected: boolean;
  cloudPullRequest: PullRequestSummary | null;
  gitStatus: NativeGitStatus | null;
  preferences: SidebarPreferences;
}) {
  const {
    store,
    preview,
    worktreeSourceRoot,
    workspace,
    messages,
    conversationTitle,
    cloudAgentSelected,
    cloudPullRequest,
    gitStatus,
    preferences,
  } = options;
  const {
    chatId,
    conversationCache,
    executionMode,
    loadingThreadIds,
    conversationStates,
    personalWorkspaceRoot,
    unreadThreadIds,
    syncedPullRequests,
    syncedThreads,
    syncedWorkspaces,
    workspacePullRequests,
  } = useStore(
    store,
    useShallow((state) => ({
      chatId: state.chatId,
      conversationCache: state.conversationCache,
      executionMode: state.executionMode,
      loadingThreadIds: state.loadingThreadIds,
      conversationStates: state.conversationStates,
      personalWorkspaceRoot: state.personalWorkspaceRoot,
      unreadThreadIds: state.unreadThreadIds,
      syncedPullRequests: state.syncedPullRequests,
      syncedThreads: state.syncedThreads,
      syncedWorkspaces: state.syncedWorkspaces,
      workspacePullRequests: state.workspacePullRequests,
    })),
  );
  const { removedProjects } = preferences;
  const activeProjectRoot = worktreeSourceRoot ?? workspace;
  const projectName = useCallback(
    (workspaceRoot: string | null, fallback: string): string => {
      if (workspaceRoot !== null && workspaceRoot === personalWorkspaceRoot) {
        return PERSONAL_WORKSPACE_NAME;
      }
      return sourceControlRepositoryFullName(workspaceRoot) ?? fallback;
    },
    [personalWorkspaceRoot],
  );
  const cachedRoots = useMemo(
    () => Object.values(conversationCache).map((entry) => entry.workspaceRoot),
    [conversationCache],
  );
  const projects = useMemo(
    () =>
      buildProjectDirectory({
        workspaces: syncedWorkspaces,
        roots: [...cachedRoots, activeProjectRoot],
        projectName,
      }),
    [activeProjectRoot, cachedRoots, projectName, syncedWorkspaces],
  );
  /** The label a folder's project goes by (what removing and restoring a project names). */
  const projectKey = useCallback(
    (workspaceRoot: string): string => projects.ofRoot(workspaceRoot).label,
    [projects],
  );
  // A conversation whose message waits to leave shows as waiting; once sent, as running.
  const waitingThreadIds = useMemo(
    () =>
      new Set(
        Object.entries(conversationStates).flatMap(([id, conversation]) =>
          conversation.pendingSend === null ? [] : [id],
        ),
      ),
    [conversationStates],
  );
  const allThreads = useMemo<readonly SidebarThread[]>(
    () =>
      buildSidebarThreads({
        preview,
        previewThreads: PREVIEW_THREADS,
        projects,
        syncedWorkspaces,
        syncedThreads,
        syncedPullRequests,
        workspacePullRequests,
        conversationCache,
        chatId,
        connectionActionRequired: hasPendingToolApproval(messages),
        loadingThreadIds,
        waitingThreadIds,
        unreadThreadIds,
        messagesLength: messages.length,
        conversationTitle,
        workspace,
        cloudAgentSelected,
        cloudPullRequest,
        executionMode: runtimeMode({ cloudAgentSelected, executionMode, gitStatus }),
        gitStatusBranch: gitStatus?.branch ?? null,
        gitStatusPullRequest: gitStatus?.pullRequest ?? null,
        now: Date.now(),
      }),
    [
      chatId,
      cloudAgentSelected,
      cloudPullRequest,
      conversationCache,
      conversationTitle,
      executionMode,
      gitStatus,
      loadingThreadIds,
      messages,
      preview,
      projects,
      syncedPullRequests,
      syncedThreads,
      syncedWorkspaces,
      unreadThreadIds,
      waitingThreadIds,
      workspace,
      workspacePullRequests,
    ],
  );
  // The sidebar lists every conversation (a plugin's runs too, the assistants' channels as their
  // rows, docs/adr-assistants.md), but group calls': Live shows them, ⌘K finds them.
  const threads = useMemo(
    () => allThreads.filter((thread) => !isAssistantGroup(thread)),
    [allThreads],
  );
  // What can be opened: every conversation, group calls included.
  const openableThreads = allThreads;
  const projectPaths = useMemo(
    () => buildProjectPaths({ preview, projects, nativeDesktop: isLocalMachine() }),
    [preview, projects],
  );
  // By repository (the View's "Repository" grouping reads the threads' own); removed ones out.
  const projectGroups = useMemo(
    () =>
      buildProjectGroups({
        preview,
        sidebarThreads: threads,
        projects,
        pinnedProjects: NO_PINNED_PROJECTS,
      }).filter(([project]) => !isProjectRemoved(project, projectPaths, removedProjects)),
    [preview, projectPaths, projects, removedProjects, threads],
  );
  // The preview's own conversation is shown as its first one; one of its threads made active (a
  // pane of a split) is itself.
  const activeThreadId =
    preview && !threads.some((thread) => thread.id === chatId) ? 'preview-active' : chatId;
  const repositoryOptions = useMemo(
    () =>
      buildRepositoryOptions(
        projectPaths,
        workspace,
        (path) => workspaceName(syncedWorkspaces, path),
        (path) => repositoryOrigin(path, projects, syncedWorkspaces),
      ),
    [projectPaths, projects, syncedWorkspaces, workspace],
  );
  return {
    openableThreads,
    activeThreadId,
    projectGroups,
    projectKey,
    projectPaths,
    repositoryOptions,
    threads,
  };
}
